import axios from 'axios';

/**
 * Instância configurada do axios para comunicação com a API.
 * Todas as requisições passam por este cliente centralizado.
 */
const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || 'https://api.dividu.app',
  headers: {
    'Content-Type': 'application/json',
  },
});

/**
 * Interceptor para adicionar token de autenticação em todas as requisições.
 */
api.interceptors.request.use(
  (config) => {
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    if (user.token) {
      config.headers['Authorization'] = `Bearer ${user.token}`;
    }
    if (user.userId) {
      config.headers['X-User-Id'] = user.userId;
    }
    
    // Log para debug (remover em produção)
    if (process.env.NODE_ENV === 'development') {
      console.log('API Request:', config.method?.toUpperCase(), config.url, {
        hasToken: !!user.token,
        userId: user.userId
      });
    }
    
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

/**
 * Interceptor para tratamento de erros globais.
 */
api.interceptors.response.use(
  (response) => response,
  (error) => {
    const requestUrl = error.response?.config?.url || '';
    
    // Tratamento de erro 401 - Não autorizado
    if (error.response?.status === 401) {
      // Não redirecionar se for erro de login (credenciais inválidas)
      // Apenas redirecionar se for token expirado/inválido em outras requisições
      const isLoginRequest = requestUrl.includes('/auth/login') || requestUrl.includes('/auth/register');
      
      if (!isLoginRequest) {
        // Token expirado ou inválido - redirecionar para login
        localStorage.removeItem('user');
        window.location.href = '/login';
      }
      
      return Promise.reject(error);
    }

    // Tratamento de erro 404 - Recurso não encontrado
    if (error.response?.status === 404) {
      console.error('Recurso não encontrado:', requestUrl);
      const errorMessage = error.response?.data?.detail || 
                          error.response?.data?.message || 
                          'Recurso não encontrado';
      error.userMessage = errorMessage;
    }

    // Tratamento de erro 403 - Acesso negado
    if (error.response?.status === 403) {
      // Não loga erro para endpoints de convite quando não autenticado
      const isInviteEndpoint = requestUrl.includes('/group-invite-links/') && requestUrl.includes('/accept');
      
      if (!isInviteEndpoint) {
        console.error('Acesso negado:', requestUrl, error.response?.data);
      }
      
      const errorMessage = error.response?.data?.detail || 
                          error.response?.data?.message || 
                          'Você não tem permissão para acessar este recurso';
      error.userMessage = errorMessage;
      
      // Log para debug apenas se não for endpoint de convite
      if (process.env.NODE_ENV === 'development' && !isInviteEndpoint) {
        console.log('403 Error Details:', {
          url: requestUrl,
          data: error.response?.data,
          headers: error.response?.headers
        });
      }
    }

    // Tratamento de erro 500 - Erro interno do servidor
    if (error.response?.status >= 500) {
      console.error('Erro no servidor:', error.response?.data);
      error.userMessage = 'Erro no servidor. Tente novamente mais tarde.';
    }

    // Tratamento de erro de rede (sem resposta do servidor)
    if (!error.response) {
      console.error('Erro de conexão:', error.message);
      error.userMessage = 'Não foi possível conectar ao servidor. Verifique sua conexão com a internet.';
    }

    return Promise.reject(error);
  }
);

/**
 * Serviços de autenticação.
 */
export const authService = {
  /**
   * Realiza login do usuário.
   */
  login: async (email, password) => {
    const response = await api.post('/auth/login', { email, password });
    localStorage.setItem('user', JSON.stringify(response.data));
    return response.data;
  },

  /**
   * Realiza logout do usuário.
   */
  logout: () => {
    localStorage.removeItem('user');
  },

  /**
   * Registra novo usuário.
   */
  register: async (userData) => {
    const response = await api.post('/auth/register', userData);
    return response.data;
  },

  /**
   * Verifica o email do usuário.
   */
  verify: async (email, code) => {
    const response = await api.post('/auth/verify', { email, code });
    if (response.data.token) {
      localStorage.setItem('user', JSON.stringify(response.data));
    }
    return response.data;
  },

  /**
   * Reenvia o código de verificação.
   */
  resendCode: async (email) => {
    const response = await api.post('/auth/resend-code', { email });
    return response.data;
  },

  /**
   * Retorna usuário logado.
   */
  getCurrentUser: () => {
    return JSON.parse(localStorage.getItem('user') || '{}');
  },

  /**
   * Verifica se o token JWT armazenado ainda é válido (não expirado).
   */
  isTokenValid: () => {
    try {
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      if (!user.token) return false;
      const payload = JSON.parse(atob(user.token.split('.')[1]));
      const nowInSeconds = Math.floor(Date.now() / 1000);
      return typeof payload.exp === 'number' && payload.exp > nowInSeconds;
    } catch {
      return false;
    }
  },

  /**
   * Solicita recuperação de senha.
   */
  forgotPassword: async (email) => {
    await api.post('/auth/forgot-password', { email });
  },

  /**
   * Reseta a senha.
   */
  resetPassword: async (email, code, newPassword) => {
    await api.post('/auth/reset-password', { email, code, newPassword });
  },
};

/**
 * Serviços de grupos.
 */
export const groupService = {
  /**
   * Cria novo grupo.
   */
  create: async (groupData) => {
    const response = await api.post('/groups', groupData);
    return response.data;
  },

  /**
   * Lista grupos do usuário autenticado.
   */
  getMyGroups: async () => {
    const response = await api.get('/groups/my-groups');
    return response.data;
  },

  /**
   * Lista grupos do usuário (mantido para compatibilidade).
   */
  getByUser: async (userId) => {
    const response = await api.get('/groups/my-groups');
    return response.data;
  },

  /**
   * Busca grupo por ID.
   */
  getById: async (groupId) => {
    const response = await api.get(`/groups/${groupId}`);
    return response.data;
  },

  /**
   * Atualiza grupo.
   */
  update: async (groupId, groupData) => {
    const response = await api.put(`/groups/${groupId}`, groupData);
    return response.data;
  },

  /**
   * Arquiva um grupo.
   */
  archive: async (groupId) => {
    await api.patch(`/groups/${groupId}/archive`);
  },

  /**
   * Deleta grupo.
   */
  delete: async (groupId) => {
    await api.delete(`/groups/${groupId}`);
  },

  /**
   * Adiciona membro ao grupo.
   */
  addMember: async (groupId, memberData) => {
    const response = await api.post(`/group-members/group/${groupId}/add`, memberData);
    return response.data;
  },

  /**
   * Lista membros do grupo.
   */
  getMembers: async (groupId) => {
    const response = await api.get(`/group-members/group/${groupId}`);
    return response.data;
  },

  /**
   * Remove membro do grupo.
   */
  removeMember: async (memberId) => {
    await api.delete(`/group-members/${memberId}`);
  },
};

/**
 * Serviços de despesas.
 */
export const expenseService = {
  /**
   * Cria nova despesa.
   */
  create: async (expenseData) => {
    const response = await api.post('/expenses/create', expenseData);
    return response.data;
  },

  /**
   * Lista despesas de um grupo.
   */
  getByGroup: async (groupId, page = 0, size = 10) => {
    const response = await api.get(`/expenses/group/${groupId}?page=${page}&size=${size}`);
    return response.data;
  },

  /**
   * Busca despesa por ID.
   */
  getById: async (expenseId) => {
    const response = await api.get(`/expenses/${expenseId}`);
    return response.data;
  },

  /**
   * Atualiza despesa.
   */
  update: async (expenseId, expenseData) => {
    const response = await api.patch(`/expenses/${expenseId}`, expenseData);
    return response.data;
  },

  /**
   * Deleta despesa.
   */
  delete: async (expenseId) => {
    await api.delete(`/expenses/${expenseId}`);
  },
};

/**
 * Serviços de saldo.
 */
export const balanceService = {
  /**
   * Calcula saldo do grupo.
   */
  calculate: async (groupId) => {
    const response = await api.get(`/balance/group/${groupId}`);
    return response.data;
  },
};

/**
 * Serviços de usuários.
 */
export const userService = {
  /**
   * Busca usuários por email (busca parcial).
   */
  searchByEmail: async (email) => {
    const response = await api.get(`/users/search?email=${encodeURIComponent(email)}`);
    return response.data;
  },

  /**
   * Lista todos os usuários.
   */
  getAll: async (page = 0, size = 20) => {
    const response = await api.get(`/users?page=${page}&size=${size}`);
    return response.data;
  },

  /**
   * Obtém a chave PIX do usuário autenticado.
   */
  getMyPixKey: async () => {
    const response = await api.get('/users/me/pix-key');
    return response.data;
  },

  /**
   * Atualiza ou cadastra a chave PIX do usuário autenticado.
   */
  saveMyPixKey: async ({ pixKey, pixKeyType }) => {
    const response = await api.post('/users/me/pix-key', { pixKey, pixKeyType });
    return response.data;
  },

  /**
   * Remove a chave PIX do usuário autenticado.
   */
  deleteMyPixKey: async () => {
    const response = await api.delete('/users/me/pix-key');
    return response.data;
  },

  /**
   * Atualiza dados do usuário (nome, telefone, senha, etc).
   */
  update: async (userId, userData) => {
    const response = await api.patch(`/users/${userId}`, userData);
    // Se a atualização foi do próprio usuário logado, vamos atualizar o cache local
    const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
    if (currentUser.userId === userId || currentUser.id === userId) {
      // Preserva o token, mas atualiza outros dados se vierem na resposta (depende da API retornar o objeto atualizado ou não)
      // Como o PATCH pode retornar no content, ou o objeto, vamos assumir que se der sucesso, atualizamos o que foi enviado
      // O ideal seria fazer um refresh do usuario, mas por hora vamos atualizar o nome se ele mudou
      if (userData.name) {
        currentUser.name = userData.name;
        localStorage.setItem('user', JSON.stringify(currentUser));
      }
    }
    return response.data;
  },
};

/**
 * Serviços de convites de grupo.
 */
export const inviteService = {
  /**
   * Cria um convite para o grupo.
   */
  create: async (groupId, invitedUserId, message = '') => {
    const response = await api.post(`/group-invites/group/${groupId}`, {
      invitedUserId,
      message,
    });
    return response.data;
  },

  /**
   * Aceita um convite.
   */
  accept: async (inviteId) => {
    const response = await api.post(`/group-invites/${inviteId}/accept`);
    return response.data;
  },

  /**
   * Recusa um convite.
   */
  decline: async (inviteId) => {
    const response = await api.post(`/group-invites/${inviteId}/decline`);
    return response.data;
  },

  /**
   * Cancela um convite.
   */
  cancel: async (inviteId) => {
    await api.delete(`/group-invites/${inviteId}`);
  },

  /**
   * Lista convites pendentes do usuário atual.
   */
  getMyInvites: async () => {
    const response = await api.get('/group-invites/my-invites');
    return response.data;
  },

  /**
   * Conta convites pendentes do usuário atual.
   */
  countMyInvites: async () => {
    const response = await api.get('/group-invites/my-invites/count');
    return response.data.count;
  },

  /**
   * Lista convites pendentes de um grupo.
   */
  getByGroup: async (groupId) => {
    const response = await api.get(`/group-invites/group/${groupId}`);
    return response.data;
  },
};

/**
 * Serviços de links de convite de grupo.
 */
export const inviteLinkService = {
  /**
   * Cria um link de convite para o grupo.
   */
  create: async (groupId, maxUses = null, expiresAt = null) => {
    const response = await api.post(`/group-invite-links/group/${groupId}`, {
      maxUses,
      expiresAt,
    });
    return response.data;
  },

  /**
   * Obtém informações do link de convite (público, sem autenticação).
   */
  getInfo: async (token) => {
    const baseURL = process.env.REACT_APP_API_URL || 'https://api.dividu.app';
    const response = await axios.get(`${baseURL}/group-invite-links/public/${token}`);
    return response.data;
  },

  /**
   * Aceita um convite através do token.
   */
  acceptByToken: async (token) => {
    const response = await api.post(`/group-invite-links/${token}/accept`);
    return response.data;
  },

  /**
   * Lista links ativos de um grupo.
   */
  getByGroup: async (groupId) => {
    const response = await api.get(`/group-invite-links/group/${groupId}`);
    return response.data;
  },

  /**
   * Desativa um link de convite.
   */
  deactivate: async (linkId) => {
    await api.delete(`/group-invite-links/${linkId}`);
  },
};

/**
 * Serviços de pagamentos PIX.
 */
export const paymentService = {
  /**
   * Pagador marca o pagamento como pago manualmente.
   */
  markManualPaid: async (paymentId) => {
    const response = await api.post(`/payments/${paymentId}/manual-paid`);
    return response.data;
  },

  /**
   * Recebedor confirma o recebimento manualmente.
   */
  markManualReceived: async (paymentId) => {
    const response = await api.post(`/payments/${paymentId}/manual-received`);
    return response.data;
  },
  /**
   * Cria um novo pagamento PIX.
   * @param {Object} paymentData - Dados do pagamento
   * @param {number} paymentData.groupId - ID do grupo
   * @param {number} paymentData.receiverId - ID do recebedor
   * @param {number} paymentData.amount - Valor do pagamento
   * @param {string} [paymentData.description] - Descrição opcional
   */
  create: async (paymentData) => {
    const response = await api.post('/payments', paymentData);
    return response.data;
  },

  /**
   * Busca um pagamento pelo ID.
   */
  getById: async (paymentId) => {
    const response = await api.get(`/payments/${paymentId}`);
    return response.data;
  },

  /**
   * Atualiza o status de um pagamento consultando o Efí.
   */
  refreshStatus: async (paymentId) => {
    const response = await api.post(`/payments/${paymentId}/refresh`);
    return response.data;
  },

  /**
   * Cancela um pagamento pendente.
   */
  cancel: async (paymentId) => {
    await api.delete(`/payments/${paymentId}`);
  },

  /**
   * Marca um pagamento como expirado.
   */
  expire: async (paymentId) => {
    const response = await api.post(`/payments/${paymentId}/expire`);
    return response.data;
  },

  /**
   * Lista pagamentos de um grupo.
   */
  getByGroup: async (groupId) => {
    const response = await api.get(`/payments/group/${groupId}`);
    return response.data;
  },

  /**
   * Lista pagamentos do usuário atual.
   */
  getMyPayments: async () => {
    const response = await api.get('/payments/my');
    return response.data;
  },

  /**
   * Reexecuta o repasse quando o recebedor já cadastrou a chave PIX.
   */
  retryTransfer: async (paymentId) => {
    const response = await api.post(`/payments/${paymentId}/retry-transfer`);
    return response.data;
  },

  /**
   * Busca informações compartilháveis de um pagamento (público, sem autenticação).
   * @param {number} paymentId - ID do pagamento
   * @param {string} privacyLevel - Nível de privacidade: 'COMPLETE', 'ANONYMOUS' ou 'HYBRID'
   */
  getShareablePayment: async (paymentId, privacyLevel = 'HYBRID') => {
    const response = await axios.get(`/api/payments/${paymentId}/share?privacyLevel=${privacyLevel}`);
    return response.data;
  },

  /**
   * Lista pagamentos aguardando confirmação do usuário atual (como recebedor).
   */
  getAwaitingConfirmation: async () => {
    const response = await api.get('/payments/awaiting-confirmation');
    return response.data;
  },

  /**
   * Conta pagamentos aguardando confirmação do usuário atual (como recebedor).
   */
  countAwaitingConfirmation: async () => {
    const response = await api.get('/payments/awaiting-confirmation/count');
    return response.data;
  },

  /**
   * Conta pagamentos aguardando confirmação do usuário atual em um grupo específico.
   */
  countAwaitingConfirmationByGroup: async (groupId) => {
    const response = await api.get(`/payments/groups/${groupId}/awaiting-confirmation/count`);
    return response.data;
  },
};

export default api;
